import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import RequirePermission from "../../auth/RequirePermission";
import { MODULES } from "../../auth/modules";
import { getStoredAuth } from "../../utils/authStorage";
import useInventoryUiConfig from "../../hooks/useInventoryUiConfig";
import FeatureGuard from "../../components/FeatureGuard";
import SchoolAdminDashboard2 from "../../Sidebar/SchoolAdminDashboard2";
import StudentsContent from "../schoolAdmin/student/StudentsContent";
import TeachersContent from "../schoolAdmin/teacher/TeachersContent";
import StaffContent from "../schoolAdmin/staff/StaffContent";
import StaffProfile from "../schoolAdmin/staff/StaffProfile";
import StaffLeaveApplication from "../Staff/Staff-leave-application";
import StaffMyAttendance from "../Staff/StaffMyAttendance";
import FeeManagementContent from "../schoolAdmin/FeeManagment/FeeManagementContent";
import AnnouncementsContent from "../schoolAdmin/Announcement/AnnouncementsContent";
import SettingsContent from "../schoolAdmin/Settings/SettingsContent";
import BillingContent from "../schoolAdmin/Billing/BillingContent";
import TenantPaymentHistory from "../schoolAdmin/Billing/TenantPaymentHistory";
import TenantInvoiceHistory from "../schoolAdmin/Billing/TenantInvoiceHistory";
import SchoolSettingsPageNew from "../../pages/SchoolSettingsPageNew";
import BusContent from "../schoolAdmin/Bus/BusContent";
import VenndorContent from "../schoolAdmin/InventoryManagment/VenndorContent";
import ClassroomMapping from "../schoolAdmin/Classroommapping/ClassroomMapping";
import HolidayScheduler from "../schoolAdmin/HolidayScheduler";
import ClassTransition from "../schoolAdmin/Transition/ClassTransition";
import TeacherAttendanceContent from "../schoolAdmin/TeacherAttendenceandleave/TeacherAttendance";
import AssetsManagement from "../schoolAdmin/InventoryManagment/AssetsManagement";
import InventoryManagement from "../schoolAdmin/InventoryManagment/InventoryManagement";
import InventoryReports from "../schoolAdmin/InventoryManagment/InventoryReports";
import InventoryAnalyticsDashboard from "../schoolAdmin/InventoryManagment/InventoryAnalyticsDashboard";
import InventoryItemDetails from "../schoolAdmin/InventoryManagment/InventoryItemDetails";
import InventorySettings from "../schoolAdmin/InventoryManagment/InventorySettings";
import InventoryImportExport from "../schoolAdmin/InventoryManagment/InventoryImportExport";
import InventoryAllTransactions from "../schoolAdmin/InventoryManagment/InventoryAllTransactions";
import CustomerLedger from "../schoolAdmin/InventoryManagment/CustomerLedger";
import CustomerLedgerDetail from "../schoolAdmin/InventoryManagment/CustomerLedgerDetail";
import VendorLedgerDetail from "../schoolAdmin/InventoryManagment/VendorLedgerDetail";
import CustomersList from "../schoolAdmin/InventoryManagment/CustomersList";
import AlertManagement from "../schoolAdmin/AlertManagment/AlertManagement";
import { ClassList } from "../schoolAdmin/Settings/ClassManagement";
import FuelManagement from "../schoolAdmin/FuelManagement/FuelManagement";
import DashboardContent from "../schoolAdmin/DashboardContent";
import VehicleManagement from "../schoolAdmin/VehicleManagement";
import EditVehicle from "../schoolAdmin/EditVehicle";
import BusTrackingMap from "../Common/BusTrackingMap";
import LiveTrackingPage from "../schoolAdmin/Bus/LiveTrackingPage";
import LiveTrackingMapPage from "../schoolAdmin/Bus/LiveTrackingMapPage";
import DriverManagement from "../schoolAdmin/Bus/DriverManagement";
import BusMergeWizard from "../schoolAdmin/Bus/MergeSession";
import FAQContent from "../schoolAdmin/FAQ/FAQContent";
import PayrollLayout from "../schoolAdmin/Payroll/PayrollLayout";
import PayrollSlipsContent from "../schoolAdmin/Payroll/PayrollSlipsContent";
import LeaveTypesManagement from "../schoolAdmin/Payroll/LeaveTypesManagement";
import SalaryConfigContent from "../schoolAdmin/Payroll/SalaryConfigContent";
import LeaveBalance from "../schoolAdmin/Payroll/LeaveBalance";
import AllowanceDeductionList from "../schoolAdmin/Payroll/AllowanceDeductionList";
import EmployeeSalarySummary from "../schoolAdmin/Payroll/EmployeeSalarySummary";
import RegistrationFacesContent from "../schoolAdmin/RegistrationFaces/RegistrationFacesContent";
import TeacherBiometricMapping from "../schoolAdmin/Settings/TeacherBiometricMapping";
import { WorkShiftUserMapping } from "../schoolAdmin/Settings/WorkShiftUserMapping";
import EditSectionPage from "../schoolAdmin/Settings/EditSectionPage";
import InventoryItemFormPage from "../schoolAdmin/InventoryManagment/InventoryItemFormPage";

function InventoryRouteGuard({ requireWrite = false, requireHistory = false, requireAnalytics = false, children }) {
  const { config, loading } = useInventoryUiConfig();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  const canRead = config?.permissions?.canRead !== false;
  const canEdit = config?.permissions?.canEdit !== false;
  const canHistory = config?.permissions?.canHistory !== false;
  const canAnalytics = config?.permissions?.canAnalytics !== false;

  if (!canRead) {
    return <Navigate to="/access-denied" replace />;
  }

  if (requireWrite && !canEdit) {
    return <Navigate to="/dashboard/inventory" replace />;
  }

  if (requireHistory && !canHistory) {
    return <Navigate to="/dashboard/inventory" replace />;
  }

  if (requireAnalytics && !canAnalytics) {
    return <Navigate to="/dashboard/inventory" replace />;
  }

  return children;
}

export default function SchoolAdminDashboardRouter({ onLogout }) {
  const [loading, setLoading] = useState(true);
  const [schoolSettings, setSchoolSettings] = useState(null);
  const location = useLocation();

  console.log("[ROUTER_DEBUG] Current pathname:", location.pathname);

  useEffect(() => {
    async function fetchSettings() {
      const { token, user } = getStoredAuth();
      if (!token || !user) {
        setSchoolSettings(null);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const schoolId = user?.school && (typeof user.school === "object" ? user.school._id : user.school);
        if (!schoolId) {
          console.error("No school found in user data");
          setLoading(false);
          return;
        }

        const res = await axiosInstance.get(API_ENDPOINTS.schoolFeatures(schoolId));

        console.log(
          "[ROUTER_DEBUG] Loaded school settings from /schools/:id/settings:",
          res.data?.data
        );
        console.log("[ROUTER_DEBUG] Settings keys available:", Object.keys(res.data?.data || {}));
        setSchoolSettings(res.data.data);
      } catch (error) {
        if (error.response?.status === 403) {
          setSchoolSettings(null);
          return;
        }
        console.error("Error fetching school settings:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchSettings();

    window.addEventListener("auth-changed", fetchSettings);
    return () => window.removeEventListener("auth-changed", fetchSettings);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-2">Loading...</span>
      </div>
    );
  }

  // If the new features Map has an explicit entry, trust it exclusively.
  // Only fall back to the legacy enableXxx field when no entry exists in the new system.
  const isFeatureOn = (newKey, legacyKey) => {
    const newFeatures = schoolSettings?.features;
    if (newFeatures && newKey in newFeatures) {
      return newFeatures[newKey]?.enabled === true;
    }
    if (legacyKey) return schoolSettings?.[legacyKey]?.enabled === true;
    return false;
  };

  // Helper function to get first available route when dashboard is disabled
  const getFirstAvailableRoute = () => {
    if (!schoolSettings) return "/dashboard/settings";

    const { user } = getStoredAuth();
    const isStaff = user?.role?.toLowerCase?.() === "staff";
    const permissions = Array.isArray(user?.permissions) ? user.permissions : [];

    const hasPermission = (permission) => {
      if (!isStaff) return true;
      if (typeof permission !== "string" || permission.length === 0) return false;
      if (permissions.includes(permission)) return true;
      if (permission.includes(":")) {
        const base = permission.split(":")[0];
        return permissions.includes(base);
      }
      if (permissions.includes(`${permission}:read`) || permissions.includes(`${permission}:write`)) return true;
      if (permission === "settings" && permissions.some((p) => typeof p === "string" && p.startsWith("settings-"))) return true;
      return false;
    };

    if (isStaff) return "/dashboard/profile";

    const candidates = [
      { enabled: isFeatureOn("inventory", null), path: "/dashboard/inventory", permission: MODULES.inventory?.permission || "inventory" },
      { enabled: isFeatureOn("staff", "enableStaff"), path: "/dashboard/staff", permission: MODULES.staff.permission },
      { enabled: isFeatureOn("students", "enableStudents"), path: "/dashboard/students", permission: MODULES.students.permission },
      { enabled: isFeatureOn("teachers", "enableTeachers"), path: "/dashboard/teachers", permission: MODULES.teachers.permission },
      { enabled: isFeatureOn("vendorManagement", null), path: "/dashboard/vendor", permission: MODULES.vendor?.permission || "vendor" },
      { enabled: isFeatureOn("assetManagement", null), path: "/dashboard/assets", permission: MODULES.assets?.permission || "assets" },
      { enabled: isFeatureOn("feeManagement", "enableFeeManagement"), path: "/dashboard/fee-management", permission: MODULES.feeManagement.permission },
      { enabled: isFeatureOn("busManagement", "enableBusService"), path: "/dashboard/bus-routes", permission: MODULES.busRoutes.permission },
      { enabled: isFeatureOn("salary", null), path: "/dashboard/payroll", permission: MODULES.payroll?.permission || "payroll" },
      { enabled: isFeatureOn("holidays", null), path: "/dashboard/holiday", permission: MODULES.holiday?.permission || "holiday" },
      { enabled: isFeatureOn("announcements", null), path: "/dashboard/announcements", permission: MODULES.announcements?.permission || "announcements" },
      { enabled: isFeatureOn("classroomMapping", "enableClassroomMapping"), path: "/dashboard/classroom-mapping", permission: MODULES.classroomMapping.permission },
    ];

    const firstAllowed = candidates.find((item) => item.enabled && hasPermission(item.permission));
    if (firstAllowed) return firstAllowed.path;

    // Settings is always available as final fallback for school admins
    return "/dashboard/settings";
  };

  return (
    <Routes>
      <Route path="/" element={<SchoolAdminDashboard2 schoolSettings={schoolSettings} onLogout={onLogout} />}>
        <Route index element={
          isFeatureOn("dashboard", "enableDashboard")
            ? <Navigate to="/dashboard/dashboard" replace />
            : <Navigate to={getFirstAvailableRoute()} replace />
        } />
        <Route path="dashboard" element={
          isFeatureOn("dashboard", "enableDashboard")
            ? <DashboardContent />
            : <Navigate to={getFirstAvailableRoute()} replace />
        } />
        <Route path="profile" element={<StaffProfile />} />
        <Route path="staff-leave" element={<FeatureGuard feature="clockInOutAttendance"><StaffLeaveApplication /></FeatureGuard>} />
        <Route path="staff-attendance" element={<FeatureGuard feature="clockInOutAttendance"><StaffMyAttendance schoolSettings={schoolSettings} /></FeatureGuard>} />
        <Route path="students" element={<FeatureGuard feature="students"><RequirePermission permission={MODULES.students.permission}><StudentsContent /></RequirePermission></FeatureGuard>} />
        <Route path="students/:studentName" element={<FeatureGuard feature="students"><RequirePermission permission={MODULES.students.permission}><StudentsContent /></RequirePermission></FeatureGuard>} />
        <Route path="teachers" element={<FeatureGuard feature="teachers"><RequirePermission permission={MODULES.teachers.permission}><TeachersContent /></RequirePermission></FeatureGuard>} />
        <Route path="teachers/:teacherName" element={<FeatureGuard feature="teachers"><RequirePermission permission={MODULES.teachers.permission}><TeachersContent /></RequirePermission></FeatureGuard>} />
        <Route path="staff" element={<FeatureGuard feature="staff"><RequirePermission permission={MODULES.staff.permission}><StaffContent /></RequirePermission></FeatureGuard>} />
        <Route path="staff/:staffName" element={<FeatureGuard feature="staff"><RequirePermission permission={MODULES.staff.permission}><StaffContent /></RequirePermission></FeatureGuard>} />
        <Route path="fee-management" element={<FeatureGuard feature="feeManagement"><RequirePermission permission={MODULES.feeManagement.permission}><FeeManagementContent /></RequirePermission></FeatureGuard>} />
        <Route path="announcements" element={<FeatureGuard feature="announcements"><RequirePermission permission={MODULES.announcements.permission}><AnnouncementsContent /></RequirePermission></FeatureGuard>} />
        <Route path="settings/sections/edit/:id" element={<RequirePermission permission={MODULES.settings.permission}><EditSectionPage /></RequirePermission>} />
        <Route path="settings/*" element={<RequirePermission permission={MODULES.settings.permission}><SettingsContent /></RequirePermission>} />
        <Route path="feature-management" element={<RequirePermission permission={MODULES.settings.permission}><SchoolSettingsPageNew /></RequirePermission>} />
        <Route path="faq" element={<RequirePermission permission={MODULES.faq.permission}><FAQContent /></RequirePermission>} />
        <Route path="billing" element={<BillingContent />} />
        <Route path="billing/payments" element={<TenantPaymentHistory />} />
        <Route path="billing/invoices" element={<TenantInvoiceHistory />} />
        <Route path="bus-routes" element={<FeatureGuard feature="busManagement"><RequirePermission permission={MODULES.busRoutes.permission}><BusContent /></RequirePermission></FeatureGuard>} />
        <Route path="vehicle-management" element={<FeatureGuard feature="busManagement"><RequirePermission permission={MODULES.busRoutes.permission}><VehicleManagement /></RequirePermission></FeatureGuard>} />
        <Route path="live-tracking" element={<FeatureGuard feature="busManagement"><RequirePermission permission={MODULES.busRoutes.permission}><LiveTrackingPage /></RequirePermission></FeatureGuard>} />
        <Route path="live-tracking/map" element={<FeatureGuard feature="busManagement"><RequirePermission permission={MODULES.busRoutes.permission}><LiveTrackingMapPage /></RequirePermission></FeatureGuard>} />
        <Route path="live-tracking/merge-session" element={<FeatureGuard feature="busManagement"><RequirePermission permission={MODULES.busRoutes.permission}><BusMergeWizard /></RequirePermission></FeatureGuard>} />
        <Route path="driver-management" element={<FeatureGuard feature="busManagement"><RequirePermission permission={MODULES.busRoutes.permission}><DriverManagement /></RequirePermission></FeatureGuard>} />
        <Route path="vendor" element={<FeatureGuard feature="vendorManagement"><RequirePermission permission={MODULES.vendor.permission}><VenndorContent /></RequirePermission></FeatureGuard>} />
        <Route path="assets" element={<FeatureGuard feature="assetManagement"><RequirePermission permission={MODULES.assets.permission}><AssetsManagement /></RequirePermission></FeatureGuard>} />
        <Route path="inventory" element={<FeatureGuard feature="inventory"><InventoryRouteGuard><InventoryManagement /></InventoryRouteGuard></FeatureGuard>} />
        <Route path="inventory/add" element={<FeatureGuard feature="inventory"><InventoryRouteGuard requireWrite><InventoryItemFormPage /></InventoryRouteGuard></FeatureGuard>} />
        <Route path="inventory/edit/:id" element={<FeatureGuard feature="inventory"><InventoryRouteGuard requireWrite><InventoryItemFormPage /></InventoryRouteGuard></FeatureGuard>} />
        <Route path="inventory/reports" element={<FeatureGuard feature="inventory"><InventoryRouteGuard><InventoryReports /></InventoryRouteGuard></FeatureGuard>} />
        <Route path="inventory/all-transaction" element={<FeatureGuard feature="inventory"><InventoryRouteGuard requireHistory><InventoryAllTransactions /></InventoryRouteGuard></FeatureGuard>} />
        <Route path="inventory/analytics" element={<FeatureGuard feature="inventory"><InventoryRouteGuard requireAnalytics><InventoryAnalyticsDashboard /></InventoryRouteGuard></FeatureGuard>} />
        <Route path="inventory/:id" element={<FeatureGuard feature="inventory"><InventoryRouteGuard><InventoryItemDetails /></InventoryRouteGuard></FeatureGuard>} />
        <Route path="inventory/settings" element={<FeatureGuard feature="inventory"><InventoryRouteGuard requireWrite><InventorySettings /></InventoryRouteGuard></FeatureGuard>} />
        <Route path="inventory/settings/import-export" element={<FeatureGuard feature="inventory"><InventoryRouteGuard requireWrite><InventoryImportExport /></InventoryRouteGuard></FeatureGuard>} />
        <Route path="inventory/ledger" element={<FeatureGuard feature="inventory"><InventoryRouteGuard><CustomerLedger /></InventoryRouteGuard></FeatureGuard>} />
        <Route path="inventory/ledger/:customerId" element={<FeatureGuard feature="inventory"><InventoryRouteGuard><CustomerLedgerDetail /></InventoryRouteGuard></FeatureGuard>} />
        <Route path="customer-ledger" element={<FeatureGuard feature="inventory"><CustomerLedger /></FeatureGuard>} />
        <Route path="customer-ledger/vendor/:vendorId" element={<FeatureGuard feature="inventory"><VendorLedgerDetail /></FeatureGuard>} />
        <Route path="customer-ledger/:customerId" element={<FeatureGuard feature="inventory"><CustomerLedgerDetail /></FeatureGuard>} />
        <Route path="customers" element={<FeatureGuard feature="inventory"><InventoryRouteGuard><CustomersList /></InventoryRouteGuard></FeatureGuard>} />
        <Route path="alert" element={<FeatureGuard feature="alerts"><RequirePermission permission={MODULES.alert.permission}><AlertManagement /></RequirePermission></FeatureGuard>} />
        <Route path="classroom-mapping" element={<RequirePermission permission={MODULES.classroomMapping.permission}><ClassroomMapping /></RequirePermission>} />
        <Route path="holiday" element={<FeatureGuard feature="holidays"><RequirePermission permission={MODULES.holiday.permission}><HolidayScheduler /></RequirePermission></FeatureGuard>} />
        <Route path="class-transition" element={<RequirePermission permission={MODULES.classTransition.permission}><ClassTransition /></RequirePermission>} />
        <Route path="teacher-attendance" element={<FeatureGuard feature="clockInOutAttendance"><RequirePermission permission={MODULES.teacherAttendance.permission}><TeacherAttendanceContent /></RequirePermission></FeatureGuard>} />
        <Route path="shift-assignment" element={<FeatureGuard feature="clockInOutAttendance"><RequirePermission permission={MODULES.teacherAttendance.permission}><WorkShiftUserMapping /></RequirePermission></FeatureGuard>} />
        <Route path="registration-faces" element={<FeatureGuard feature="webcamAttendance"><RequirePermission permission={MODULES.registrationFaces.permission}><RegistrationFacesContent /></RequirePermission></FeatureGuard>} />
        <Route
          path="biometric-integration"
          element={
            <FeatureGuard feature="biometricIntegration" redirectTo={getFirstAvailableRoute()}>
              <RequirePermission permission={MODULES.settings.permission}>
                <TeacherBiometricMapping />
              </RequirePermission>
            </FeatureGuard>
          }
        />
        <Route path="class-management" element={<RequirePermission permission={MODULES.classManagement.permission}><ClassList /></RequirePermission>} />
        <Route path="vehicles" element={<FeatureGuard feature="busManagement"><RequirePermission permission={MODULES.busRoutes.permission}><VehicleManagement /></RequirePermission></FeatureGuard>} />
        <Route path="vehicles/:id/edit" element={<FeatureGuard feature="busManagement"><RequirePermission permission={MODULES.busRoutes.permission}><EditVehicle /></RequirePermission></FeatureGuard>} />
        <Route path="fuel-management" element={<FeatureGuard feature="busManagement"><RequirePermission permission={MODULES.busRoutes.permission}><FuelManagement /></RequirePermission></FeatureGuard>} />
        <Route path="payroll" element={<FeatureGuard feature="salary"><RequirePermission permission={MODULES.payroll.permission}><PayrollLayout /></RequirePermission></FeatureGuard>}>
          <Route index element={<Navigate to="/dashboard/payroll/payslip" replace />} />
          <Route path="payslip" element={<PayrollSlipsContent />} />
          <Route path="leave-types" element={<LeaveTypesManagement />} />
          <Route path="salary-structure" element={<SalaryConfigContent />} />
          <Route path="leave-balance" element={<LeaveBalance />} />
          <Route path="allowance-deduction" element={<AllowanceDeductionList />} />
          <Route path="salary-summary" element={<EmployeeSalarySummary />} />
        </Route>
        <Route path="*" element={<Navigate to={getFirstAvailableRoute()} replace />} />
      </Route>
    </Routes>
  );
} 